import { supabase } from '../lib/supabase';

const TABLE_NAME = 'messages';

// messages FKs point to auth.users; we fetch user details from public.users (same ids)
// to avoid "Could not find a relationship between 'messages' and 'users'" (PGRST200)
function toUser(obj) {
  if (!obj) return null;
  return { id: obj.id, email: obj.email, full_name: obj.full_name, role: obj.role };
}

async function fetchUserMap(userIds) {
  if (!userIds?.length) return {};
  const uniq = [...new Set(userIds)];
  const { data, error } = await supabase
    .from('users')
    .select('id, email, full_name, role')
    .in('id', uniq);
  if (error) return {};
  const map = {};
  (data || []).forEach((u) => { map[u.id] = toUser(u); });
  return map;
}

// Get all messages for the current user (conversations they're part of)
export const getUserMessages = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: rows, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (error) throw error;
    const list = rows || [];

    const userIds = [...new Set(list.flatMap((m) => [m.sender_id, m.recipient_id]).filter(Boolean))];
    const userMap = await fetchUserMap(userIds);

    return list.map((m) => ({
      ...m,
      sender: userMap[m.sender_id] || toUser({ id: m.sender_id, email: null, full_name: null, role: null }),
      recipient: userMap[m.recipient_id] || toUser({ id: m.recipient_id, email: null, full_name: null, role: null }),
    }));
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// Get conversation between two users
export const getConversation = async (otherUserId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: rows, error } = await supabase
      .from(TABLE_NAME)
      .select('*')
      .or(`and(sender_id.eq.${user.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (error) throw error;
    const list = rows || [];

    const userIds = [...new Set(list.flatMap((m) => [m.sender_id, m.recipient_id]).filter(Boolean))];
    const userMap = await fetchUserMap(userIds);

    return list.map((m) => ({
      ...m,
      sender: userMap[m.sender_id] || toUser({ id: m.sender_id, email: null, full_name: null, role: null }),
      recipient: userMap[m.recipient_id] || toUser({ id: m.recipient_id, email: null, full_name: null, role: null }),
    }));
  } catch (error) {
    console.error('Error fetching conversation:', error);
    throw error;
  }
};

// Get list of users the current user can message (techs can message managers/admins, managers/admins can message anyone)
export const getMessageableUsers = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get current user's role - check both user_metadata and users table
    let currentRole = user?.user_metadata?.role || 'user';
    
    try {
      const { data: currentUser, error: roleError } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (!roleError && currentUser?.role) {
        // Prefer role from users table if available
        currentRole = currentUser.role;
      }
    } catch (err) {
      // If users table query fails, use user_metadata role
      console.warn('Could not fetch role from users table, using user_metadata:', err);
    }

    // Build query for all users except current user
    // Try to get all users first, then filter by role
    let query = supabase
      .from('users')
      .select('id, email, full_name, role')
      .neq('id', user.id);

    // Techs (role === 'user') can only message managers and admins
    // Managers and admins can message anyone
    if (currentRole === 'user') {
      query = query.in('role', ['manager', 'admin']);
    }
    // If manager or admin, get all users (no role filter)

    // Add ordering
    query = query.order('full_name', { ascending: true });

    const { data, error } = await query;
    
    if (error) {
      console.error('Error querying messageable users:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      
      // If RLS is blocking, try a different approach - get all users and filter client-side
      // This is a fallback if RLS policies are too restrictive
      if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('policy')) {
        console.warn('RLS policy may be blocking query. Attempting fallback...');
        // For now, return empty array - RLS policies need to be updated
        return [];
      }
      
      throw error;
    }
    
    // If no results and user is a tech, log warning
    if ((!data || data.length === 0) && currentRole === 'user') {
      console.warn('No managers or admins found. This might indicate:');
      console.warn('1. No users with manager/admin roles exist');
      console.warn('2. RLS policies are blocking the query');
      console.warn('3. Users table does not have the expected data');
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching messageable users:', error);
    // Return empty array instead of throwing to prevent UI crashes
    return [];
  }
};

// Send a message
export const sendMessage = async (recipientId, message) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: row, error } = await supabase
      .from(TABLE_NAME)
      .insert([{
        sender_id: user.id,
        recipient_id: recipientId,
        message: message,
        is_read: false,
      }])
      .select('*')
      .single();

    if (error) throw error;

    const userMap = await fetchUserMap([user.id, recipientId]);
    return {
      ...row,
      sender: userMap[row.sender_id] || toUser({ id: row.sender_id, email: null, full_name: null, role: null }),
      recipient: userMap[row.recipient_id] || toUser({ id: row.recipient_id, email: null, full_name: null, role: null }),
    };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Mark message as read
export const markMessageAsRead = async (messageId) => {
  try {
    const { error } = await supabase
      .from(TABLE_NAME)
      .update({ is_read: true })
      .eq('id', messageId);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

// Mark all messages from a specific user as read
export const markConversationAsRead = async (otherUserId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from(TABLE_NAME)
      .update({ is_read: true })
      .eq('recipient_id', user.id)
      .eq('sender_id', otherUserId)
      .eq('is_read', false);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking conversation as read:', error);
    throw error;
  }
};

// Get unread message count
export const getUnreadMessageCount = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { count, error } = await supabase
      .from(TABLE_NAME)
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', user.id)
      .eq('is_read', false);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error getting unread message count:', error);
    throw error;
  }
};

// Get conversations list (unique users with latest message)
export const getConversations = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get all messages
    const messages = await getUserMessages();
    
    // Group by other user and get latest message
    const conversationsMap = {};
    messages.forEach(msg => {
      const otherUserId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
      const otherUser = msg.sender_id === user.id ? msg.recipient : msg.sender;
      
      if (!conversationsMap[otherUserId]) {
        conversationsMap[otherUserId] = {
          userId: otherUserId,
          user: otherUser,
          lastMessage: msg,
          unreadCount: 0,
        };
      }
      
      // Update if this message is newer
      if (new Date(msg.created_at) > new Date(conversationsMap[otherUserId].lastMessage.created_at)) {
        conversationsMap[otherUserId].lastMessage = msg;
      }
      
      // Count unread messages
      if (msg.recipient_id === user.id && !msg.is_read) {
        conversationsMap[otherUserId].unreadCount += 1;
      }
    });

    return Object.values(conversationsMap).sort((a, b) => 
      new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
    );
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};
